import * as _ from "lodash";

import { ExtensionBase } from "../extensionBase";
import { UiUtils } from "../../common/uiUtils";
import { Misc } from "../../common/misc";
import { PropertyModel as Model } from "../../model/propertyFinder";
import { viewerOptions } from "../../viewer";
import { ViewerUtils } from "../../common/viewerUtils";
import * as SmartDrawings from "../../smartDrawings";
import { ModelApi } from "../../api/modelApi";
import ExternalData = SmartDrawings.ExternalData;

// Extension for updating external data for elements
// Ruukki-case only !!!!
export class ExternalDataExtension extends ExtensionBase {
    static extensionName = "ExternalDataExtension";

    private subToolbar: Autodesk.Viewing.UI.ControlGroup;
    private externalDataButton: Autodesk.Viewing.UI.Button;

    private propertyFinder: Model.PropertyFinder;

    private productionId: string;

    private extData: ExternalData;
    private tempExtData: ExternalData;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: any) {
        super(viewer, options);
    }

    static registerExtension() {
        super.registerExtension(ExternalDataExtension.extensionName, ExternalDataExtension);
    }

    load(): boolean {
        if (this.hasUrlParam(SmartDrawings.SdUrlParameters.RUUKKI_PRODUCTION_ID)) {
            const id = this.getUrlParamValue(SmartDrawings.SdUrlParameters.RUUKKI_PRODUCTION_ID);
            this.productionId = id;
        }

        console.log(`${ExternalDataExtension.extensionName} is loaded!`);
        return super.load();
    };

    unload(): boolean {
        return super.unload();
    };

    async onToolbarCreated() {
        this.externalDataButton = UiUtils.createButton({
            id: "SmartDrawings.ExternalDataButton",
            tooltip: "External Data",
            iconClass: "ion-md-basket",
            onClick: () => {
                if (this.externalDataButton.getState() === Autodesk.Viewing.UI.Button.State.ACTIVE) {
                    this.stopSelecting();
                }
            }
        });

        if (this.productionId) {
            this.subToolbar = this.getControlGroup("SmartDrawings.Extensions");
            this.subToolbar.addControl(this.externalDataButton);

            if (viewerOptions.externalData) {
                await this.startSelecting();
            } else {
                this.viewer.addEventListener(SmartDrawings.SdEvents.PARAMS_TO_DBIDS_MAPPED, this.onExternalDataLoaded.bind(this));
            }
        }

        //this.viewer.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, this.printElementInfo.bind(this));
    }

    async onExternalDataLoaded() {
        if (this.productionId) {
            try {
                await ModelApi.hasRightsToEditModel(viewerOptions.guid);
                await this.startSelecting();
            } catch (err) {
                console.log(err.response);
            }
        }
    }

    private async startSelecting() {
        this.extData = viewerOptions.externalData;

        const result = await ModelApi.getExternalData(viewerOptions.guid, "externaldata_temp.json")
            .catch(err => console.log(err.response));

        if (result) {
            const data = result.data;

            if (data) {
                this.tempExtData = data;

                // Find type and package to search
                const tempProps = this.tempExtData.propertyValues;
                if (this.productionId in tempProps) {
                    const elementType = tempProps[this.productionId].TypeId;
                    const elementPackage = tempProps[this.productionId].PackageId;

                    const props = this.extData.propertyValues;

                    let nodeIds = [];
                    const ifcIds = _.keys(props);
                    for (let ifcId of ifcIds) {
                        if (elementType === props[ifcId].TypeId && elementPackage === props[ifcId].PackageId) {
                            const prodId = props[ifcId].ProductionId;
                            if (prodId === null || prodId === this.productionId) {
                                nodeIds.push(this.extData.paramsToDbIds[ifcId]);
                            }
                        }
                    }

                    this.initView();

                    if (nodeIds.length > 0) {
                        this.externalDataButton.setState(Autodesk.Viewing.UI.Button.State.ACTIVE);

                        this.viewer.isolate(nodeIds);

                        UiUtils.notifySuccess("Valitse asennettava paneeli");
                        this.viewer.addEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this.selectionListener);
                    } else {
                        UiUtils.notifyWarning("Ei valittavia paneeleja");
                    }
                } else {
                    UiUtils.notifyWarning("Ei valittavia paneeleja");
                }
            }
        }
    }

    private stopSelecting() {
        this.externalDataButton.setState(Autodesk.Viewing.UI.Button.State.INACTIVE);
        this.viewer.removeEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this.selectionListener);
        this.viewer.showAll();
    }

    private async updateExtData(elementId: number) {
        //const selectors: SmartDrawings.ParentSelector[] = [{ displayCategory: "Item", displayName: "Type", displayValue: "IFCBEAM" }];
        //const id = await ViewerUtils.findNodeMatchingProp(elementId, selectors, this.viewer);

        // Try to find IfcId to update
        let ifcId = await this.findNodeIfcId(elementId);

        if (ifcId) {
            const props = this.extData.propertyValues;

            // If same selection as last time -> nothing to save
            if (props[ifcId].ProductionId !== this.productionId) {
                let updatedElements = [];
                const updateData = {};
                updateData[ifcId] = { ProductionId: this.productionId, Status: "INSTALLED" };
                updatedElements.push(updateData);

                // Try to find same production id with different ifc-id (previously selected)
                const oldIfcId = _.keys(props).filter(p => p !== ifcId && props[p].ProductionId === this.productionId);
                if (oldIfcId.length > 0) {
                    updateData[oldIfcId[0]] = { ProductionId: null, Status: "ORDERED" };
                }

                UiUtils.notifySuccess(`Paneeli ${ifcId} valittu ja tietoja päivitetään`);
                const result = await ModelApi.updateExternalData(viewerOptions.guid, updateData).catch(err => console.log(err.response));

                if (result) {
                    const data = result.data;
                    const extIdMapping = viewerOptions.externalData.paramsToDbIds;
                    viewerOptions.externalData = data;
                    viewerOptions.externalData.paramsToDbIds = extIdMapping;
                }
            } else {
                ifcId = null;
                UiUtils.notifySuccess(`Valittiin sama paneeli kuin aiemmin`);
            }
        }

        this.externalDataButton.setState(Autodesk.Viewing.UI.Button.State.INACTIVE);
        this.viewer.removeEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this.selectionListener);
        this.viewer.showAll();

        return ifcId;
    }

    /*
     * Temporary fix to avoid too large steps when zooming with mouse wheel or panning
     * Possible cause might be found in Viewer.autocam.dir vector
     * Occur with Revit models
     * TODO: Fix this more general way
     */
    private async initView() {
        var filter = { viewport: true };
        var state = this.viewer.getState(filter);
        this.viewer.fitToView();
        this.viewer.restoreState(state, filter, false);
    }

    private async findNodeIfcId(nodeId: number) {
        const newIfcId = _.keys(this.extData.paramsToDbIds).filter(id => this.extData.paramsToDbIds[id] === nodeId);
        if (newIfcId.length > 0) {
            return newIfcId[0];
        }

        // Or try to find data in the parent.
        const parentId = await ViewerUtils.getNodeParent(nodeId, this.viewer);
        if (parentId != null) {
            return await this.findNodeIfcId(parentId);
        }

        return null;
    }

    selectionListener = () => {
        const element = this.viewer.getSelection();
        if (element.length === 1) {
            //console.log(element);
            this.updateExtData(element[0]).then((res) => {
                if (res) {
                    UiUtils.notifySuccess(`Paneelin ${res} tiedot päivitetty`);
                }
            });
        }
    }

    private getPropertiesFromModel = (modelNode: SmartDrawings.IModelNode): Promise<Autodesk.Viewing.Property[]> => {
        return new Promise((resolve, reject) => {
            this.viewer.model.getProperties(
                modelNode.nodeId,
                result => resolve(result.properties),
                error => reject(error));
        });
    }

    private hasUrlParam(paramName: string): boolean {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.has(paramName);
    }

    /**
     * Get value of the url parameter
     * @param paramName Name of the query string parameter
     */
    private getUrlParamValue(paramName: string): string {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(paramName);
    }

    private async printElementInfo() {
        console.log("Kopioidaan id:t ja tyypit leikepöydälle. Liimaa ne vaikka...");
        const instanceTree = this.viewer.model.getInstanceTree();
        const dbIds = Object.keys(instanceTree.nodeAccess.dbIdToIndex).map(id => Number(id));
        //console.log(dbIds);
        const criterion: Model.PropertyCriterion[] = [
            {
                types: ["IFCBEAM"],
                typeSelectors: [],
                "properties": [
                    {
                        "displayCategory": "IFC",
                        "finalCategory": "General",
                        "displayNames": [{
                            "original": "GLOBALID",
                            "final": "IFC-ID",
                            "formatter": null
                        }],
                        "summed": false
                    },
                    {
                        "displayCategory": "Identity Data",
                        "finalCategory": "General",
                        "displayNames": [{
                            "original": "Mark",
                            "final": "Type-ID",
                            "formatter": null
                        }],
                        "summed": false
                    }
                ]
            }
        ];

        this.propertyFinder = new Model.PropertyFinder(this.getPropertiesFromModel);

        let propertiesAndTitle;
        let props;
        let elements: Array<{ id: string; type: string }> = [];
        for (let i = 0; i < dbIds.length; i++) {
            if (dbIds[i]) {
                propertiesAndTitle = await this.propertyFinder.getNodeProperties(
                    new SmartDrawings.ModelNode(this.viewer.model.id, dbIds[i]), [], criterion);
                props = propertiesAndTitle.properties;
                if (props.length > 0) {
                    //console.log(propertiesAndTitle);
                    if (props[1] == undefined)
                        console.log(props);
                    else {
                        elements.push({
                            id: props[0].displayValue,
                            type: props[1].displayValue
                        });
                    }
                }
            }
        }

        //console.log(elements);

        const unique = _.uniqWith(elements, _.isEqual);
        let result: string = "";
        for (let j = 0; j < unique.length; j++) {
            //console.log(`${unique[j].id}\t${unique[j].type}`);
            result = result.concat(`${unique[j].id}\t${unique[j].type}\n`);
        }

        navigator.clipboard.writeText(result).then().catch(e => console.error(e));
        //console.log(unique);

        this.viewer.removeEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, this.printElementInfo);
    }
}